import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Variants, Transition } from 'framer-motion';
import { Typography, Button as MuiButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useThemeContext } from './ThemeWrapper';

import Sidebar from './Sidebar';
import RightPanel from './RightPanel';

// Type for a katakana tip item: [character, romaji]
type KatakanaTipItem = [string, string];

// Type for a section of tips
type KatakanaTipSection = {
    title: string;
    data: KatakanaTipItem[];
};

const Katakana101: React.FC = () => {
    const navigate = useNavigate();
    const { isDarkTheme } = useThemeContext();

    const katakana101Data: KatakanaTipSection[] = [
        {
            title: 'Katakana Basic Sounds',
            data: [['ア', 'a'], ['イ', 'i'], ['ウ', 'u'], ['エ', 'e'], ['オ', 'o']],
        },
        {
            title: 'Katakana Dakuten and Handakuten',
            data: [
                ['ガ', 'ga'], ['ギ', 'gi'], ['グ', 'gu'], ['ゲ', 'ge'], ['ゴ', 'go'],
                ['ザ', 'za'], ['ジ', 'ji'], ['ズ', 'zu'], ['ゼ', 'ze'], ['ゾ', 'zo'],
            ],
        },
        {
            title: 'Katakana Combo Sounds',
            data: [
                ['キャ', 'kya'], ['キュ', 'kyu'], ['キョ', 'kyo'],
                ['シャ', 'sha'], ['シュ', 'shu'], ['ショ', 'sho'],
            ],
        },
        {
            title: 'Small ッ in Katakana',
            data: [
                ['ッ+k', 'kk'], ['ッ+s', 'ss'], ['ッ+t', 'tt'], ['ッ+p', 'pp'],
            ],
        },
        {
            title: 'Long Vowels in Katakana',
            data: [
                ['アー', 'aa'], ['イー', 'ii'], ['ウー', 'uu'], ['エー', 'ee'], ['オー', 'oo'],
            ],
        },
        {
            title: 'Common Loanword Katakana',
            data: [
                ['ファ', 'fa'], ['フィ', 'fi'], ['フェ', 'fe'], ['フォ', 'fo'],
                ['チェ', 'che'], ['ティ', 'ti'], ['トゥ', 'tu'],
            ],
        },
    ];

    const playAudio = (romaji: string) => {
        if (!romaji) return;
        const cleanRomaji = romaji.split('/')[0].toLowerCase().trim();
        const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${cleanRomaji}.mp3`);
        audio.play().catch(err => {
            console.error(`Audio not found: ${cleanRomaji}.mp3`, err);
        });
    };

    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.05 },
        },
    };

    const springTransition: Transition = {
        type: "spring",
        stiffness: 100,
        damping: 10
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: springTransition },
    };

    return (
        <div
            className={`flex min-h-screen font-inter transition-colors duration-300 ${
                isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'
            }`}
        >
            <Sidebar />

            <main className="flex-1 p-8 pb-20 md:p-12 overflow-y-auto">
                {/* Mobile Back Button */}
                <motion.div
                    className="md:hidden absolute top-4 left-4 sm:top-8 sm:left-8 z-10 mt-8"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 100, damping: 10 }}
                >
                    <MuiButton
                        variant="contained"
                        startIcon={<ArrowLeft size={20} />}
                        onClick={() => navigate("/hiragana")}
                        className={`!font-bold !py-2 !px-4 !rounded-xl !shadow-md ${
                            isDarkTheme
                                ? '!bg-gray-800/70 hover:!bg-gray-700/70 !text-white'
                                : '!bg-gray-300/70 hover:!bg-gray-400/70 !text-black'
                        }`}
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Back
                    </MuiButton>
                </motion.div>

                {/* Page Title */}
                <Typography variant="h3" component="h1" className="!font-extrabold mb-2">
                    Katakana Tips
                </Typography>
                <Typography
                    variant="body1"
                    component="p"
                    className={`text-lg mb-6 ${
                        isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                    }`}
                >
                    See all the tips about Katakana
                </Typography>

                {/* Katakana Tip Sections */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key="katakana-tips-sections"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={containerVariants}
                    >
                        {katakana101Data.map((section, sectionIndex) => (
                            <React.Fragment key={section.title || `section-${sectionIndex}`}>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    className="!font-extrabold mt-8 mb-4"
                                >
                                    {section.title}
                                </Typography>
                                <motion.div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 md:gap-6">
                                    {section.data.map((item, itemIndex) => (
                                        <motion.div
                                            key={`${section.title}-${itemIndex}`}
                                            className={`p-4 rounded-xl shadow-lg border-2 border-transparent cursor-pointer transition-transform duration-200 ${
                                                isDarkTheme
                                                    ? 'bg-zinc-900 hover:border-emerald-400'
                                                    : 'bg-white hover:border-emerald-600'
                                            }`}
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            whileTap={{ scale: 0.95 }}
                                            variants={itemVariants}
                                            onClick={() => playAudio(item[1])}
                                        >
                                            <div className="text-3xl font-bold text-center mb-1">{item[0]}</div>
                                            <div
                                                className={`text-sm text-center mb-1 ${
                                                    isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                                                }`}
                                            >
                                                {item[1]}
                                            </div>
                                            <div
                                                className={`h-1 rounded-full mt-2 ${
                                                    isDarkTheme ? 'bg-gray-700' : 'bg-gray-300'
                                                }`}
                                            >
                                                <div
                                                    className={`h-full w-0 rounded-full transition-all duration-500 ${
                                                        isDarkTheme
                                                            ? 'bg-emerald-400'
                                                            : 'bg-emerald-600'
                                                    }`}
                                                ></div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            </React.Fragment>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </main>

            <RightPanel />
        </div>
    );
};

export default Katakana101;
